import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput, PrismTooltip } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import { t } from 'ember-intl';
import HotelBenchmarkInsightUpsellIcon from 'lumen/components/hotel/benchmark-insight/upsell-icon';
import type { KpiCategory } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

export type DropdownSegmentOption = Option & {
  id: KpiCategory;
};

interface HotelBenchmarkInsightDropdownsDropdownSegmentSignature {
  Args: {
    onSegmentSelectionChanged: (
      selectedSegmentOption: DropdownSegmentOption,
    ) => void;
    segmentSelectionOptions: DropdownSegmentOption[];
    selectedSegmentOption: DropdownSegmentOption & {
      fullPath: string;
      value: string;
    };
  };
}

export default class HotelBenchmarkInsightDropdownsDropdownSegment extends Component<HotelBenchmarkInsightDropdownsDropdownSegmentSignature> {
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];

  get isDisabled(): boolean {
    return Boolean(
      this.benchmarkInsightFeatures.dropdownSegmentDisabledTooltip,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipContent() {
    const { selectedSegmentOption } = this.args;
    const { dropdownSegmentDisabledTooltip } = this.benchmarkInsightFeatures;

    if (dropdownSegmentDisabledTooltip) {
      return dropdownSegmentDisabledTooltip;
    }

    if (selectedSegmentOption?.fullPath !== selectedSegmentOption?.value) {
      return selectedSegmentOption.fullPath;
    }

    return null;
  }

  <template>
    {{#let
      (component
        HotelBenchmarkInsightUpsellIcon
        tooltipText=this.benchmarkInsightFeatures.beniLightDropdownSegmentTooltipText
      )
      as |afterBackOptionComponent|
    }}
      <PrismComboSelectInput
        @disabled={{this.isDisabled}}
        @onChange={{@onSegmentSelectionChanged}}
        @options={{@segmentSelectionOptions}}
        @unsafe_afterBackOptionComponent={{if
          this.benchmarkInsightFeatures.showTrialTeasers
          afterBackOptionComponent
          undefined
        }}
        @unsafe_collapsibleGroupBackLabel={{t "general.back"}}
        @unsafe_isClampedToOneLine={{true}}
        @value={{@selectedSegmentOption}}
        data-test-id="dropdown-segment"
      />
    {{/let}}

    {{#if this.tooltipContent}}
      <PrismTooltip @text={{this.tooltipContent}} />
    {{/if}}
  </template>
}
